import { useState, useCallback, useRef, useEffect } from 'react';
import { BeatDetector } from '../utils/beatDetector';
import type { BeatAnalysis } from '../types';

interface UseBeatDetectorReturn {
    isAnalyzing: boolean;
    error: string | null;
    beats: number[];
    bpm: number;
    duration: number;
    waveformData: number[];
    analyzeBeats: (audioFile: File) => Promise<BeatAnalysis>;
    getTransitionTimestamps: (videoDuration: number, clipCount: number) => number[];
    generateWaveformData: (width?: number) => number[];
    reset: () => void;
}

/**
 * Custom hook for beat detection and audio analysis
 */
export function useBeatDetector(): UseBeatDetectorReturn {
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [beats, setBeats] = useState<number[]>([]);
    const [bpm, setBpm] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [waveformData, setWaveformData] = useState<number[]>([]);

    const detectorRef = useRef<BeatDetector | null>(null);
    const mountedRef = useRef(true);

    // Initialize detector
    useEffect(() => {
        if (!detectorRef.current) {
            detectorRef.current = new BeatDetector();
        }

        return () => {
            // Only dispose detector, don't set mountedRef to false
            // mountedRef should only be false when component truly unmounts
            if (detectorRef.current) {
                detectorRef.current.dispose();
                detectorRef.current = null;
            }
        };
    }, []);

    // Track component mount status separately
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    });

    /**
     * Analyze audio file for beats
     */
    const analyzeBeats = useCallback(async (audioFile: File): Promise<BeatAnalysis> => {
        if (!detectorRef.current) {
            throw new Error('Beat detector not initialized');
        }

        setIsAnalyzing(true);
        setError(null);

        try {
            const analysis = await detectorRef.current.analyzeBeats(audioFile);

            if (mountedRef.current) {
                setBeats(analysis.beats);
                setBpm(analysis.bpm);
                setDuration(analysis.duration);

                // Generate waveform data
                const waveform = detectorRef.current.generateWaveformData(1000);
                setWaveformData(waveform);
            }

            return analysis;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to analyze beats';
            if (mountedRef.current) {
                setError(errorMessage);
            }
            throw err;
        } finally {
            if (mountedRef.current) {
                setIsAnalyzing(false);
            }
        }
    }, []);

    /**
     * Get transition timestamps for video clips
     */
    const getTransitionTimestamps = useCallback(
        (videoDuration: number, clipCount: number): number[] => {
            if (!detectorRef.current) {
                return [];
            }
            return detectorRef.current.getTransitionTimestamps(videoDuration, clipCount);
        },
        []
    );

    /**
     * Generate waveform data for visualization
     */
    const generateWaveformData = useCallback((width: number = 1000): number[] => {
        if (!detectorRef.current) {
            return [];
        }
        return detectorRef.current.generateWaveformData(width);
    }, []);

    /**
     * Reset state
     */
    const reset = useCallback(() => {
        setBeats([]);
        setBpm(0);
        setDuration(0);
        setWaveformData([]);
        setError(null);
        setIsAnalyzing(false);

        if (detectorRef.current) {
            detectorRef.current.dispose();
            detectorRef.current = new BeatDetector();
        }
    }, []);

    return {
        isAnalyzing,
        error,
        beats,
        bpm,
        duration,
        waveformData,
        analyzeBeats,
        getTransitionTimestamps,
        generateWaveformData,
        reset,
    };
}
